import { z } from 'zod';

import type {
  DeclarativeFormConfig,
  FormFieldConfig,
  FormFieldConfigComponentProps,
  SupportedComponentType,
} from './types';
import type { ConditionalVisibility, CustomerTaskDefinitionBase, CustomerTaskField } from '../types/customerTask';

export type { ConditionalVisibility, CustomerTaskField } from '../types/customerTask';
export type CustomerTaskForm = CustomerTaskDefinitionBase;

function getConditionFieldValue(
  fieldName: string,
  values: Record<string, unknown>,
  topLevelName: string | undefined,
  isArrayField: boolean | undefined,
  cleanName: string | undefined
): unknown {
  if (topLevelName) {
    if (isArrayField) {
      const arr = values[cleanName!] as Record<string, unknown>[] | undefined;
      return arr?.[0]?.[fieldName];
    }
    const nested = values[topLevelName] as Record<string, unknown> | undefined;
    return nested?.[fieldName];
  }
  return values[fieldName];
}

function matchesCondition(condition: ConditionalVisibility, fieldValue: unknown): boolean {
  const normalizedFieldValue = typeof fieldValue === 'boolean' ? String(fieldValue) : fieldValue;
  switch (condition.conditionType) {
    case 'equals':
      return normalizedFieldValue === condition.value;
    case 'contains':
      if (Array.isArray(normalizedFieldValue)) return normalizedFieldValue.includes(condition.value);
      return typeof normalizedFieldValue === 'string' && normalizedFieldValue.includes(condition.value as string);
    case 'oneof':
      return Array.isArray(condition.value) && condition.value.includes(normalizedFieldValue as string);
  }
}

function createDynamicFieldConfig(config: {
  component: SupportedComponentType;
  name: string;
  label: string;
  description: string | undefined;
  componentProps: FormFieldConfigComponentProps | undefined;
  hidden: boolean | undefined;
  conditionallyShown: ConditionalVisibility | undefined;
  topLevelName: string | undefined;
  isArrayField: boolean | undefined;
  cleanName: string | undefined;
}): FormFieldConfig<Record<string, unknown>> {
  const { conditionallyShown, topLevelName, isArrayField, cleanName, ...rest } = config;
  return {
    ...rest,
    shouldRender: conditionallyShown
      ? (values) => {
          const fieldValue = getConditionFieldValue(
            conditionallyShown.fieldName,
            values as Record<string, unknown>,
            topLevelName,
            isArrayField,
            cleanName
          );
          return matchesCondition(conditionallyShown, fieldValue);
        }
      : undefined,
  } as FormFieldConfig<Record<string, unknown>>;
}

const ARRAY_COMPONENT_TYPES: SupportedComponentType[] = ['multipleSelector', 'multiSelectCheckbox'];
const FILE_COMPONENT_TYPES: SupportedComponentType[] = ['file', 'upload'];

function isArrayComponentType(type: SupportedComponentType): boolean {
  return ARRAY_COMPONENT_TYPES.includes(type);
}

/**
 * radioGroupList is also used with string-valued options (e.g. enum choices), so only
 * treat it as a boolean field when every option is actually a boolean (e.g. a Yes/No group).
 */
function isBooleanRadioGroupList(field: CustomerTaskField): boolean {
  if (field.declarativeFormWidget.type !== 'radioGroupList') return false;
  const options = (field.declarativeFormWidget.componentProps as { options?: { value: unknown }[] } | undefined)
    ?.options;
  return Array.isArray(options) && options.length > 0 && options.every((option) => typeof option.value === 'boolean');
}

function sanitizeFieldName(name: string): string {
  return name.replace(/\[\]$/, '');
}

// Monorail flags a resumed file/upload field via componentProps.existingUploadLabel — an
// opaque placeholder string (e.g. "File already uploaded"), never the real filename or content.
function getExistingUploadLabel(field: CustomerTaskField): string | undefined {
  const { type, componentProps } = field.declarativeFormWidget;
  if (!FILE_COMPONENT_TYPES.includes(type)) return undefined;
  const explicitLabel = (componentProps as { existingUploadLabel?: unknown } | undefined)?.existingUploadLabel;
  return typeof explicitLabel === 'string' ? explicitLabel : undefined;
}

/**
 * Converts Monorail's polymorphic customer-task field metadata into a
 * DeclarativeForm config.
 */
export function createCustomerTaskFormConfig(
  task: CustomerTaskForm
): DeclarativeFormConfig<Record<string, unknown>> | null {
  if (!task.fields || task.fields.length === 0) return null;

  const topLevelName = task.name;
  const isArrayField = topLevelName?.includes('[]');
  const cleanName = topLevelName?.replace('[]', '');
  const fieldSchemas: Record<string, z.ZodTypeAny> = {};

  task.fields.forEach((field) => {
    const { type: componentType, hidden } = field.declarativeFormWidget;
    const cleanFieldName = sanitizeFieldName(field.name);
    const isRequired = field.required && !hidden;
    let fieldSchema: z.ZodTypeAny;

    if (componentType === 'checkbox' && isRequired) {
      fieldSchema = z.boolean().refine((value) => value === true, {
        message: `${field.label || cleanFieldName} must be checked`,
      });
    } else if (componentType === 'switch' || componentType === 'checkbox' || isBooleanRadioGroupList(field)) {
      fieldSchema = z.boolean();
    } else if (isArrayComponentType(componentType)) {
      fieldSchema = isRequired
        ? z.array(z.string()).min(1, `${field.label || cleanFieldName} is required`)
        : z.array(z.string()).optional();
    } else if (FILE_COMPONENT_TYPES.includes(componentType)) {
      // An existing upload satisfies "required" even though the field's form value stays
      // undefined until the user selects a new file — see getExistingUploadLabel.
      const hasExistingUpload = getExistingUploadLabel(field) !== undefined;
      fieldSchema = isRequired
        ? z.any().refine(
            (value) =>
              hasExistingUpload || (value != null && value !== '' && !(Array.isArray(value) && value.length === 0)),
            { message: `${field.label || cleanFieldName} is required` }
          )
        : z.any().optional();
    } else if (
      typeof field.declarativeFormWidget.value === 'number' ||
      (componentType === 'input' &&
        field.declarativeFormWidget.componentProps != null &&
        'type' in field.declarativeFormWidget.componentProps &&
        field.declarativeFormWidget.componentProps.type === 'number')
    ) {
      fieldSchema = isRequired
        ? z.preprocess(
            (value) => (value === '' ? undefined : Number(value)),
            z.number({ error: `${field.label || cleanFieldName} is required` })
          )
        : z.preprocess((value) => (value === '' ? undefined : value), z.coerce.number().optional());
    } else {
      fieldSchema = isRequired
        ? z.string().min(1, `${field.label || cleanFieldName} is required`)
        : z.string().optional();
    }

    fieldSchemas[cleanFieldName] = fieldSchema;
  });

  const nestedSchema = z.object(fieldSchemas);
  const schema = topLevelName
    ? isArrayField
      ? z.object({ [cleanName!]: z.array(nestedSchema) })
      : z.object({ [topLevelName]: nestedSchema })
    : nestedSchema;

  const rows = task.fields.map((field) => {
    const cleanFieldName = sanitizeFieldName(field.name);
    const name = topLevelName
      ? isArrayField
        ? `${cleanName}.0.${cleanFieldName}`
        : `${topLevelName}.${cleanFieldName}`
      : cleanFieldName;
    const { type, componentProps, hidden, conditionallyShown } = field.declarativeFormWidget;
    const existingUploadLabel = getExistingUploadLabel(field);

    return [
      createDynamicFieldConfig({
        cleanName,
        component: type,
        componentProps: existingUploadLabel !== undefined ? { ...componentProps, existingUploadLabel } : componentProps,
        conditionallyShown,
        description: field.description,
        hidden,
        isArrayField,
        label: field.label || field.name,
        name,
        topLevelName,
      }),
    ];
  });

  const defaultFieldValues = Object.fromEntries(
    task.fields.map((field) => {
      const { type, value } = field.declarativeFormWidget;
      const emptyValue =
        type === 'switch' || type === 'checkbox'
          ? false
          : isArrayComponentType(type)
            ? []
            : FILE_COMPONENT_TYPES.includes(type) || isBooleanRadioGroupList(field)
              ? undefined
              : '';
      const resolvedValue = getExistingUploadLabel(field) !== undefined ? emptyValue : value ?? emptyValue;
      return [sanitizeFieldName(field.name), resolvedValue];
    })
  );

  const defaultValues = topLevelName
    ? isArrayField
      ? { [cleanName!]: [defaultFieldValues] }
      : { [topLevelName]: defaultFieldValues }
    : defaultFieldValues;

  return { defaultValues, rows, schema };
}
